import {
    IssueHardwareType,
    IssueSavingsStatus,
    IssueStatusType,
} from '@kcftech/prometheustypescriptapiclient';
import { DataSourceSort } from 'custom_hooks/data_hooks/data_source/data_source';
import { IssueType } from 'pages/Desk/issues/shared';

export type TimeRangeFilter = 'past-12-hours' | 'past-day' | 'past-week' | 'past-month';

export type SeverityFilter = 'severe' | 'moderate' | 'low';

export type PriorityFilter = 'critical' | 'high' | 'medium' | 'low' | 'minimal';

export type StatusFilter = 'new' | 'open' | 'closed' | 'wont-fix' | 'not-an-issue';

export type HealthRangeFilter = 'added' | 'not-added';

export type VisibilityFilter = 'kcf-only' | 'public';

export type FlaggedFilter = 'flagged' | 'flagged-with-notifications';

export type CreatedByFilter = 'user-generated' | 'system-generated';

export type ResolutionFilter = 'resolved' | 'unresolved';

export type IssueFilters = {
    timeRangeFilter: TimeRangeFilter | undefined;
    statusFilters: StatusFilter[];
    severityFilters: SeverityFilter[];
    priorityFilters: PriorityFilter[];
    savingsFilters: IssueSavingsStatus[];
    faultTypeFilters: string[];
    issueLocationFilters: string[];
    dynamicSystemFilters: string[];
    healthRangeFilter: HealthRangeFilter | undefined;
    hardwareTypeFilters: IssueHardwareType[];
    visibilityFilter: VisibilityFilter | undefined;
    flaggedFilters: FlaggedFilter[];
    createdByFilter: CreatedByFilter | undefined;
    resolvedFilter: ResolutionFilter | undefined;
};

/**
 * Issue status constants for filter transformations
 */
export const ACTIVE_ISSUE_STATUSES = [IssueStatusType.New, IssueStatusType.Open];

export const ARCHIVED_ISSUE_STATUSES = [
    IssueStatusType.Closed,
    IssueStatusType.WontFix,
    IssueStatusType.NotIssue,
];

/**
 * Base type for all issues state values - extends IssueFilters with additional state properties
 * that are shared between asset and assembly modals
 */
export type BaseIssuesStateValues = IssueFilters & {
    // Additional common properties
    sort: DataSourceSort[];
};

/**
 * Base methods for managing issues state - contains the common filter setters
 * that are shared between asset and assembly modals
 */
export type BaseIssuesStateMethods = {
    // Individual filter setters
    setStatusFilters: (statusFilters: StatusFilter[]) => void;
    setSeverityFilters: (severityFilters: SeverityFilter[]) => void;
    setPriorityFilters: (priorityFilters: PriorityFilter[]) => void;
    setSavingsFilters: (savingsFilters: IssueSavingsStatus[]) => void;
    setTimeRangeFilter: (timeRangeFilter: TimeRangeFilter | undefined) => void;
    setHealthRangeFilter: (healthRangeFilter: HealthRangeFilter | undefined) => void;
    setFaultTypeFilters: (faultTypeFilters: string[]) => void;
    setIssueLocationFilters: (issueLocationFilters: string[]) => void;
    setDynamicSystemFilters: (dynamicSystemFilters: string[]) => void;
    setHardwareTypeFilters: (hardwareTypeFilters: IssueHardwareType[]) => void;
    setVisibilityFilter: (visibilityFilter: VisibilityFilter | undefined) => void;
    setFlaggedFilters: (flaggedFilters: FlaggedFilter[]) => void;
    setCreatedByFilter: (createdByFilter: CreatedByFilter | undefined) => void;
    setResolvedFilter: (resolvedFilter: ResolutionFilter | undefined) => void;
    setSort: (sort: DataSourceSort[]) => void;

    // Batch update method for performance optimization
    onFiltersChange: (newFilters: IssueFilters) => void;
};

export type BaseIssuesState = BaseIssuesStateValues & BaseIssuesStateMethods;

/**
 * Modal-specific extensions to the base issues state
 * Includes tab management for switching between different issue types
 */
export type ModalIssuesState = BaseIssuesState & {
    tabIndex: number;
    currentTabType: IssueType;
    setTabIndex: (tabIndex: number) => void;
};
